import { $ZodError } from "zod/v4/core";
import { flattenError, z } from "zod/v4-mini";
import { Logger } from "@/utils/logger";

const SDKIframeOptionsSchema = z.object({
  backgroundColor: z.string(),
});

const SDKOptionsSchema = z.object({
  clientId: z.string(),
  apiBase: z.optional(z.string()),
  appOrigin: z.optional(z.string()),
  iframe: z.optional(SDKIframeOptionsSchema),
  hostLoginFn: z.optional(z.unknown()),
  hostRewardsFn: z.optional(z.unknown()),
});

type SDKIframeOptions = z.infer<typeof SDKIframeOptionsSchema>;
type SDKBaseOptions = z.infer<typeof SDKOptionsSchema>;

const defaultIframeOptions = {
  backgroundColor: "var(--color-brand-background)",
} satisfies SDKIframeOptions;

const defaultOptions = {
  apiBase: "https://api.fanfest.vip",
  appOrigin: "https://fanfest.vip",
  iframe: defaultIframeOptions,
} satisfies Partial<SDKBaseOptions>;

export class SDKOptions {
  clientId: string;
  apiBase: string;
  appOrigin: string;
  iframe: SDKIframeOptions;
  hostLoginFn: (() => void) | undefined;
  hostRewardsFn: (() => void) | undefined;
  #logger: Logger;

  get graphqlUrl() {
    return `${this.apiBase}/graphql`;
  }

  constructor(options: unknown, logger: Logger) {
    this.#logger = logger.extend(this.constructor.name);

    try {
      const parsedOptions = SDKOptionsSchema.parse(options);
      const mergedOptions = { ...defaultOptions, ...parsedOptions };

      this.clientId = mergedOptions.clientId;
      this.apiBase = mergedOptions.apiBase;
      this.appOrigin = mergedOptions.appOrigin;
      this.iframe = mergedOptions.iframe;

      if (parsedOptions.hostLoginFn instanceof Function) {
        // Ensure the function is bound to the window object to not expose the function to the global scope
        this.hostLoginFn = (mergedOptions.hostLoginFn as () => void).bind(
          window,
        );
      }

      if (parsedOptions.hostRewardsFn instanceof Function) {
        this.hostRewardsFn = (mergedOptions.hostRewardsFn as () => void).bind(
          window,
        );
      }
    } catch (error) {
      if (error instanceof $ZodError) {
        this.#logger.error(
          "Error parsing SDK options",
          flattenError(error).fieldErrors,
        );
        throw new Error(this.#logger.prefixMessage("Invalid SDK options"));
      } else {
        throw error;
      }
    }
  }
}
